import { type MouseEvent, useEffect } from "react";

import { t } from "@impero-com/common/utils/i18n";
import type { NavItem } from "@impero-com/ui";
import {
    Sidebar,
    SidebarBrandButton,
    SidebarHamburger,
    SidebarMenuItem,
    SidebarNavGroup,
    SidebarSubMenuItem,
    navDestination,
    navTrail,
    useLocalStorage,
} from "@impero-com/ui";
import type { NavRowId } from "@impero-com/ui/domain";
import { useNavigate } from "react-router-dom";

import { HelpMenu } from "../../components/HelpMenu";
import { isLegacyPath } from "../../legacyRoutes";

import { NavIcon, navIcons } from "./navIcons";

interface NavSidebarProps {
    items: readonly NavItem[];
    /** The row of the page that is open, `null` on a page with no row. */
    activeId: NavRowId | null;
    /** Navigate in-app rather than reloading. Legacy paths always reload. */
    routerLinks: boolean;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function NavSidebar({
    items,
    activeId,
    routerLinks,
    collapsed,
    onToggleCollapsed,
}: NavSidebarProps) {
    const navigate = useNavigate();
    const [openGroups, setOpenGroups] = useLocalStorage<
        Record<string, boolean>
    >("sidebar:openGroups", {});

    // A closed group would hide the active row; opening it persists, so the
    // group does not snap shut again when the user leaves the page.
    const activeGroup =
        activeId === null ? undefined : navTrail(items, activeId).at(-2);
    useEffect(() => {
        if (activeGroup !== undefined) {
            setOpenGroups((groups) => ({ ...groups, [activeGroup.id]: true }));
        }
    }, [activeGroup, setOpenGroups]);

    function handleClick(href: string | undefined) {
        return (event: MouseEvent<HTMLElement>) => {
            if (!routerLinks || href === undefined || isLegacyPath(href)) {
                return;
            }
            event.preventDefault();
            navigate(href);
        };
    }

    const rowType = collapsed ? "iconOnly" : "noCaret";

    return (
        <Sidebar
            collapsed={collapsed}
            navLabel={t("Navigation_Menu")}
            brand={
                <SidebarBrandButton
                    label={t("Navigation_ToggleMenu")}
                    aria-expanded={!collapsed}
                    onClick={onToggleCollapsed}
                >
                    <SidebarHamburger />
                </SidebarBrandButton>
            }
            bottom={
                <HelpMenu
                    trigger={
                        <SidebarMenuItem
                            label={t("Help")}
                            icon={<NavIcon src={navIcons.help} />}
                            type={rowType}
                        />
                    }
                />
            }
        >
            {items.map((item) => {
                const destination = navDestination(item);

                if (item.children === undefined) {
                    return (
                        <SidebarMenuItem
                            key={item.id}
                            label={item.label}
                            icon={item.icon}
                            type={rowType}
                            href={destination}
                            onClick={handleClick(destination)}
                            active={item.id === activeId}
                            locked={item.locked}
                        />
                    );
                }

                return (
                    <SidebarNavGroup
                        key={item.id}
                        label={item.label}
                        icon={item.icon}
                        collapsed={collapsed}
                        active={item.id === activeGroup?.id}
                        caretLabel={t("Navigation_ToggleSection")}
                        expanded={openGroups[item.id] ?? true}
                        onExpandedChange={(expanded) =>
                            setOpenGroups((groups) => ({
                                ...groups,
                                [item.id]: expanded,
                            }))
                        }
                        href={destination}
                        onHeaderClick={(event) => {
                            setOpenGroups((groups) => ({
                                ...groups,
                                [item.id]: true,
                            }));
                            handleClick(destination)(event);
                        }}
                    >
                        {item.children.map((child) => (
                            <SidebarSubMenuItem
                                key={child.id}
                                label={child.label}
                                href={navDestination(child)}
                                onClick={handleClick(navDestination(child))}
                                count={child.count}
                                active={child.id === activeId}
                                locked={child.locked}
                            />
                        ))}
                    </SidebarNavGroup>
                );
            })}
        </Sidebar>
    );
}
